import type { TravelFormData } from "@/portals/customer/types/customer-portal";
import {
  ModeOfTravel,
  PlanType,
  TravelPurpose,
  TripType,
  type TripApplication,
  type TripCategory,
} from "@/shared/types/applicationTripTypes";
import { autoFormatPhoneNumber } from "@/shared/utils/phoneUtils";
import type {
  PrincipalBeneficiary,
  PrincipalBeneficiaryResponseType,
  PrincipalFamilyMember,
  PrincipalFamilyMemberResponseType,
  PrincipalInsured,
  PrincipalInsuredAddress,
  PrincipalInsuredAddressResponseType,
  PrincipalInsuredContactInfo,
  PrincipalInsuredContactInfoResponseType,
  PrincipalInsuredResponseType,
} from "@/types/app";
import { addDays, format } from "date-fns";
import { intervalToDuration } from "date-fns";

export const transformPrincipalInsuredFromResponse = (
  principalData: PrincipalInsuredResponseType
): PrincipalInsured => {
  return {
    principalId: principalData.principalId,
    title: principalData.title,
    firstName: principalData.firstName,
    middleName: principalData.middleName,
    lastName: principalData.lastName,
    suffix: principalData.suffix,
    ...(principalData.birthdate
      ? {
          dateOfBirth: format(`${principalData.birthdate}`, "MM/dd/yyyy"),
        }
      : {}),
    gender: principalData.gender,
    occupation: principalData.occupation,
    nationality: principalData.nationality,
    civilStatus: principalData.civilStatus,
    idType: principalData.idType,
    idNumber: principalData.idNumber,
  };
};

export const transformPrincipalInsuredAddressFromResponse = (
  principalAddressData: PrincipalInsuredAddressResponseType,
  skipHouseUnitNo?: boolean
): PrincipalInsuredAddress => {
  return {
    houseUnitNo: skipHouseUnitNo ? "" : principalAddressData.address1,
    subdivisionVillage: "",
    streetName: principalAddressData.address1,
    barangay: principalAddressData.barangay,
    city: principalAddressData.city,
    province: principalAddressData.province,
    zipCode: principalAddressData.zipCode,
    country: principalAddressData.country,
  };
};

export const transformPrincipalInsuredContactInfoFromResponse = (
  principalContactInfoData: PrincipalInsuredContactInfoResponseType
): PrincipalInsuredContactInfo => {
  const includeMobileCountryCode = principalContactInfoData.contactCd !== "+63";
  const formatResultMobileNum = autoFormatPhoneNumber(
    principalContactInfoData.contactCd + principalContactInfoData.contactNumber,
    "mobile",
    includeMobileCountryCode
  );

  const includeLandlineCountryCode =
    principalContactInfoData.landlineCd !== "+63";

  const formatResultLandlineNum = autoFormatPhoneNumber(
    principalContactInfoData.landline || "",
    "landline",
    includeLandlineCountryCode
  );

  return {
    contactType: "mobile", // TODO: not sure if used or not
    countryCode: principalContactInfoData.contactCd,
    phoneNumber: principalContactInfoData.contactNumber,
    formattedPhoneNumber: formatResultMobileNum.formatted,
    landlineCountryCode: principalContactInfoData.landline
      ? principalContactInfoData.landlineCd
      : "",
    landlinePhoneNumber: principalContactInfoData.landline
      ? principalContactInfoData.landline
      : "",
    formattedLandlinePhoneNumber: principalContactInfoData.landline
      ? formatResultLandlineNum.formatted
      : "",
  };
};

export const transformPrincipalBeneficiariesFromResponse = (
  principalBeneficiariesData: PrincipalBeneficiaryResponseType
): PrincipalBeneficiary => {
  return {
    id: `${principalBeneficiariesData.beneficiaryId}`,
    title: principalBeneficiariesData.title || "",
    firstName: principalBeneficiariesData.firstName,
    middleName: principalBeneficiariesData.middleName,
    lastName: principalBeneficiariesData.lastName,
    suffix: principalBeneficiariesData.suffix || "N/A",
    relationship: principalBeneficiariesData.relationship,
  };
};

export const transformPrincipalFamilyMemberFromResponse = (
  principalFamilyMemberData: PrincipalFamilyMemberResponseType,
  principalAddress: PrincipalInsuredAddressResponseType
): PrincipalFamilyMember => {
  const duration = intervalToDuration({
    start: principalFamilyMemberData.birthDate,
    end: new Date(),
  });

  const age = duration.years;

  const memberAddress = principalFamilyMemberData.address;

  const shareAddressWithPrincipal =
    principalAddress.address1 === memberAddress.address1 &&
    principalAddress.barangay === memberAddress.barangay &&
    principalAddress.city === memberAddress.city &&
    principalAddress.country === memberAddress.country &&
    principalAddress.province === memberAddress.province &&
    principalAddress.zipCode === memberAddress.zipCode;

  return {
    id: `${principalFamilyMemberData.familyId}`,
    title: principalFamilyMemberData.title,
    firstName: principalFamilyMemberData.firstName,
    middleName: principalFamilyMemberData.middleName,
    lastName: principalFamilyMemberData.lastName,
    suffix: principalFamilyMemberData.suffix,
    ...(principalFamilyMemberData.birthDate
      ? {
          dateOfBirth: format(
            `${principalFamilyMemberData.birthDate}`,
            "MM/dd/yyyy"
          ),
        }
      : {}),
    age: `${age}`,
    gender: principalFamilyMemberData.gender,
    idType: principalFamilyMemberData.idType,
    idNumber: principalFamilyMemberData.idNumber,
    shareAddressWithPrincipal: shareAddressWithPrincipal,
    nationality: principalFamilyMemberData.nationality,
    ...transformPrincipalInsuredAddressFromResponse(memberAddress, false),
  };
};

export const parseFormData = (formData: TravelFormData): TripApplication => {
  const tripType =
    formData.tripType === "Annual Multi-Trip"
      ? TripType.MultiTrip
      : TripType.SingleTrip;

  const isMultiTrip = tripType === TripType.MultiTrip;

  console.log("parseFormData", isMultiTrip);
  const startDate = isMultiTrip
    ? new Date(formData.travelDate)
    : new Date(formData.startDate);

  const endDate = isMultiTrip
    ? addDays(startDate, 89)
    : new Date(formData.endDate);

  const parseValue: TripApplication = {
    category: formData.tripCategory as TripCategory,
    tripType: tripType,
    startTrip: formData.startTrip,
    destination: formData.destination,
    endTrip: formData.endTrip,
    endDate: endDate,
    startDate: startDate,
    isReturningToPhilippines: formData.philippinesEndpoint === "Yes",
    modeOfTravel: formData.transportMode
      ? formData.transportMode === "air"
        ? ModeOfTravel.ViaAir
        : ["land", "sea"].includes(formData.transportMode)
        ? ModeOfTravel.ViaLand
        : undefined
      : undefined,
    planType: formData.planType ? (formData.planType as PlanType) : undefined,
    purpose: formData.purposeOfTravel
      ? (formData.purposeOfTravel as TravelPurpose)
      : undefined,
  };

  console.log("parseFormData", formData, parseValue);

  return parseValue;
};
